import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Network, Plus, Loader2, Trash2, Upload, Smartphone, Lock, Link as LinkIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, shortId } from '@/lib/utils';

export default function Proxies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['proxies'], queryFn: () => api.get('/proxies').then(r => r.data) });
  const [open, setOpen] = React.useState(false);
  const [bulkOpen, setBulkOpen] = React.useState(false);

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/proxies/${id}`),
    onSuccess: () => { toast.success('Proxy removed'); qc.invalidateQueries({ queryKey: ['proxies'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to remove'),
  });

  const bulkDelMut = useMutation({
    mutationFn: (body) => api.delete('/proxies/bulk', { data: body }).then(r => r.data),
    onSuccess: (r) => {
      toast.success(`Removed ${r.deleted} proxies`, {
        description: r.skipped ? `Skipped ${r.skipped} bound to active agents` : undefined,
      });
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['proxies'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Bulk delete failed'),
  });

  const [selected, setSelected] = React.useState(() => new Set());
  const items = data?.items || [];
  const selectableIds = items.filter(p => !p.bound_agent).map(p => p.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="proxies-page">
      <PageHeader
        eyebrow="Infrastructure"
        title="Proxies"
        description="Dedicated proxy pool — strictly 1 proxy ↔ 1 agent. Mobile carrier IPs recommended for cold Reddit warmups."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setBulkOpen(true)} data-testid="proxies-bulk-import-button">
              <Upload className="mr-2 h-4 w-4" /> Bulk import
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="proxies-new-button">
              <Plus className="mr-2 h-4 w-4" /> Add proxy
            </Button>
          </div>
        }
      />

      {/* Summary pills */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="proxies-summary">
        <SummaryPill label="Total" value={summary.total || 0} />
        <SummaryPill label="Available" value={summary.available || 0} accent="success" />
        <SummaryPill label="Bound" value={summary.bound || 0} accent="primary" />
        <SummaryPill label="Mobile" value={summary.mobile || 0} accent="info" icon={Smartphone} />
        <SummaryPill label="Archived" value={summary.archived || 0} accent="muted" />
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-white/5 bg-[hsl(var(--surface-2))] px-4 py-2.5" data-testid="proxies-bulk-action-bar">
            <div className="text-sm text-[hsl(var(--text-2))]">
              <span className="font-semibold text-[hsl(var(--text-1))]" data-testid="proxies-bulk-selected-count">{selected.size}</span> selected
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="proxies-bulk-clear-button">Clear</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive" data-testid="proxies-bulk-delete-button">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete {selected.size}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selected.size} {selected.size === 1 ? 'proxy' : 'proxies'}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. Proxies bound to active agents will be skipped automatically.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="confirm-dialog-cancel-button">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => bulkDelMut.mutate({ ids: Array.from(selected) })}
                      data-testid="confirm-dialog-confirm-button"
                    >
                      {bulkDelMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading…</div>
        ) : !data?.items?.length ? (
          <EmptyState icon={Network} title="No proxies configured"
            description="Add your first proxy or bulk-import a vendor list. Every agent needs its own dedicated IP."
            action={
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>Bulk import</Button>
                <Button size="sm" onClick={() => setOpen(true)}>Add proxy</Button>
              </div>
            }
          />
        ) : (
          <Table data-testid="proxies-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
                    onCheckedChange={toggleAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Select all unbound"
                    data-testid="proxies-select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Bound to</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={p.id} data-testid={`proxies-row-${p.id}`} className={p.is_active ? '' : 'opacity-60'}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={() => toggleOne(p.id)}
                      disabled={!!p.bound_agent}
                      aria-label={p.bound_agent ? `Cannot select bound proxy ${p.label}` : `Select ${p.label}`}
                      data-testid={`proxies-select-${p.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium flex items-center gap-1.5">
                      {p.label}
                      {p.is_mobile && <Smartphone className="h-3 w-3 text-[hsl(var(--primary))]" aria-label="Mobile" />}
                    </div>
                    <div className="font-mono text-xs text-[hsl(var(--text-3))]">{shortId(p.id)}</div>
                  </TableCell>
                  <TableCell><span className="font-mono text-xs">{p.provider}</span></TableCell>
                  <TableCell><span className="font-mono text-xs text-[hsl(var(--text-2))]">{p.endpoint}</span></TableCell>
                  <TableCell>
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                      p.is_mobile
                        ? 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                        : 'border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))]'}`}>
                      {p.is_mobile ? 'mobile' : (p.proxy_type || 'http')}
                    </span>
                  </TableCell>
                  <TableCell><span className="text-xs uppercase tracking-wider">{p.country}</span></TableCell>
                  <TableCell>
                    {p.bound_agent ? (
                      <span className="inline-flex items-center gap-1 text-xs">
                        <LinkIcon className="h-3 w-3 text-[hsl(var(--primary))]" />
                        <span className="font-medium">{p.bound_agent.name}</span>
                      </span>
                    ) : !p.is_active ? (
                      <span className="text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">archived</span>
                    ) : (
                      <span className="text-[11px] uppercase tracking-wider text-[hsl(var(--success))]">available</span>
                    )}
                  </TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{formatDate(p.created_at)}</span></TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="rounded p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--danger))] transition-colors" aria-label="Delete" data-testid={`proxies-delete-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove proxy?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {p.bound_agent
                              ? `This proxy is bound to '${p.bound_agent.name}'. Unbind or archive the agent first.`
                              : 'This action cannot be undone.'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="confirm-dialog-cancel-button">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => delMut.mutate(p.id)} data-testid="confirm-dialog-confirm-button">Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AddProxyDrawer open={open} onClose={() => setOpen(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ['proxies'] }); setOpen(false); }} />
      <BulkImportDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => { qc.invalidateQueries({ queryKey: ['proxies'] }); }} />
    </div>
  );
}


function SummaryPill({ label, value, accent = 'default', icon: Icon }) {
  const hue = accent === 'success' ? 'text-[hsl(var(--success))]'
    : accent === 'primary' ? 'text-[hsl(var(--primary))]'
    : accent === 'info' ? 'text-sky-400'
    : accent === 'muted' ? 'text-[hsl(var(--text-3))]'
    : 'text-[hsl(var(--text-1))]';
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] px-4 py-3" data-testid={`summary-pill-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-3))]">
        {Icon && <Icon className="h-3 w-3" />}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular ${hue}`}>{value}</div>
    </div>
  );
}


function AddProxyDrawer({ open, onClose, onCreated }) {
  const [form, setForm] = React.useState({
    label: '', provider: 'decodo', endpoint: '', username: '', password: '',
    country: 'us', sticky: true, proxy_type: 'http', is_mobile: true,
  });
  const mut = useMutation({
    mutationFn: (p) => api.post('/proxies', p),
    onSuccess: () => { toast.success('Proxy added'); onCreated(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-[520px] flex flex-col" data-testid="right-drawer">
        <SheetHeader>
          <SheetTitle>Add proxy</SheetTitle>
          <SheetDescription>Connection details are encrypted at rest. This proxy will be 1-to-1 with one agent.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="space-y-1.5"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Decodo Mobile 10001" data-testid="proxy-label-input" /></div>
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
              <SelectTrigger data-testid="proxy-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="decodo">Decodo (ex-Smartproxy)</SelectItem>
                <SelectItem value="smartproxy">Smartproxy</SelectItem>
                <SelectItem value="brightdata">Bright Data</SelectItem>
                <SelectItem value="iproyal">IPRoyal</SelectItem>
                <SelectItem value="soax">SOAX</SelectItem>
                <SelectItem value="netnut">NetNut</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Endpoint (host:port)</Label><Input value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} placeholder="us.decodo.com:10001" data-testid="proxy-endpoint-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="proxy-username-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="proxy-password-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.proxy_type} onValueChange={(v) => setForm({ ...form, proxy_type: v })}>
                <SelectTrigger data-testid="proxy-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="http">HTTP / HTTPS</SelectItem>
                  <SelectItem value="socks5">SOCKS5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Country (ISO)</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toLowerCase() })} maxLength={4} /></div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-white/5 bg-[hsl(var(--surface-2))] px-3 py-2">
            <div>
              <Label className="text-sm">Mobile carrier IP</Label>
              <div className="text-[11px] text-[hsl(var(--text-3))]">Highest trust — required for cold reddit warmups.</div>
            </div>
            <Switch checked={form.is_mobile} onCheckedChange={(v) => setForm({ ...form, is_mobile: v })} data-testid="proxy-is-mobile-switch" />
          </div>
        </div>
        <SheetFooter className="border-t border-white/5 pt-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending || !form.label || !form.endpoint} data-testid="drawer-submit-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add proxy
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}


function BulkImportDialog({ open, onClose, onImported }) {
  const [text, setText] = React.useState('');
  const [provider, setProvider] = React.useState('decodo');
  const [country, setCountry] = React.useState('us');
  const [isMobile, setIsMobile] = React.useState(true);
  const [proxyType, setProxyType] = React.useState('http');

  const mut = useMutation({
    mutationFn: (body) => api.post('/proxies/bulk-import', body).then(r => r.data),
    onSuccess: (r) => {
      toast.success(`Imported ${r.created} proxies`, {
        description: r.skipped || r.errors ? `Skipped ${r.skipped}, errors ${r.errors}` : undefined,
      });
      setText('');
      onImported();
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Import failed'),
  });

  const submit = () => {
    const lines = text.split('\n').map(s => s.trim()).filter(Boolean);
    if (!lines.length) { toast.error('Paste one proxy per line'); return; }
    mut.mutate({
      items: lines.map(raw => ({ raw })),
      default_provider: provider,
      default_country: country,
      default_is_mobile: isMobile,
      default_proxy_type: proxyType,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import proxies</DialogTitle>
          <DialogDescription>
            Paste one proxy per line in the format <span className="font-mono text-xs">host:port:user:pass</span> or <span className="font-mono text-xs">host:port</span>. Each becomes a dedicated 1-to-1 proxy for an agent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Default provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger data-testid="bulk-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="decodo">Decodo</SelectItem>
                  <SelectItem value="smartproxy">Smartproxy</SelectItem>
                  <SelectItem value="brightdata">Bright Data</SelectItem>
                  <SelectItem value="iproyal">IPRoyal</SelectItem>
                  <SelectItem value="soax">SOAX</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Default country</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value.toLowerCase())} maxLength={4} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Default type</Label>
              <Select value={proxyType} onValueChange={setProxyType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="http">HTTP / HTTPS</SelectItem>
                  <SelectItem value="socks5">SOCKS5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-between gap-2 rounded-md border border-white/5 bg-[hsl(var(--surface-2))] px-3 py-2">
              <div>
                <Label className="text-xs">Mobile IPs</Label>
                <div className="text-[10px] text-[hsl(var(--text-3))]">Carrier IPs are higher trust.</div>
              </div>
              <Switch checked={isMobile} onCheckedChange={setIsMobile} data-testid="bulk-mobile-switch" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Proxies (one per line)</Label>
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="us.decodo.com:10001:user-spt1081uam-sessionduration-60:zz9QgjtF37ChPi3y_z"
              className="font-mono text-xs" data-testid="bulk-proxies-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mut.isPending || !text.trim()} data-testid="bulk-submit-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
